import React, { FC } from "react";

const SectionHeading: FC<{ children: React.ReactNode }> = ({ children }) => (
	<h2 className="text-2xl font-SatoshiBold text-headerblue mt-12 mb-4">
		{children}
	</h2>
);

const SubHeading: FC<{ children: React.ReactNode }> = ({ children }) => (
	<h3 className="text-lg font-SatoshiBold text-headerblue mt-6 mb-2">
		{children}
	</h3>
);

const Paragraph: FC<{ children: React.ReactNode }> = ({ children }) => (
	<p className="font-SatoshiRegular text-[#475467] leading-relaxed mb-5">
		{children}
	</p>
);

const Bullets: FC<{ children: React.ReactNode }> = ({ children }) => (
	<ul className="list-disc pl-6 space-y-2 mb-6 font-SatoshiRegular text-[#475467] leading-relaxed">
		{children}
	</ul>
);

const Strong: FC<{ children: React.ReactNode }> = ({ children }) => (
	<span className="font-SatoshiBold text-headerblue">{children}</span>
);

const MailLink: FC<{ email: string }> = ({ email }) => (
	<a
		href={`mailto:${email}`}
		className="text-bblue font-SatoshiMedium hover:underline"
	>
		{email}
	</a>
);

const PrivacyContent = () => {
	return (
		<section className="content-section bg-white px-6 pt-16 pb-16 lg:pb-24 lg:pt-20">
			<div className="max-w-4xl mx-auto">
				<div className="flex flex-col gap-1 mb-10 font-SatoshiMedium text-bash text-sm">
					<span>
						<Strong>Effective date:</Strong> 3 July 2026
					</span>
					<span>
						<Strong>Last updated:</Strong> 3 July 2026
					</span>
				</div>

				<Paragraph>
					This Privacy Policy explains how <Strong>Bankole Technologies</Strong>{" "}
					("Bankolé", "we", "our", or "us") collects, uses, shares, and protects
					your information when you visit{" "}
					<a
						href="https://www.usebankole.com"
						target="_blank"
						rel="noreferrer"
						className="text-bblue font-SatoshiMedium hover:underline"
					>
						www.usebankole.com
					</a>
					, use the Bankolé web platform, mobile applications, or any related
					products and services (collectively, the "Services").
				</Paragraph>
				<Paragraph>
					Bankolé is a construction project-management platform that helps
					construction companies, contractors, consultants, developers, and
					project teams manage projects, tasks, budgets, documentation, site
					media, communication, and other construction workflows.
				</Paragraph>
				<Paragraph>
					By accessing or using our Services, you agree to this Privacy Policy.
					If you do not agree with this Policy, please do not use our Services.
				</Paragraph>

				<SectionHeading>1. Information we collect</SectionHeading>

				<SubHeading>1.1 Information you provide</SubHeading>
				<Paragraph>
					Depending on how you interact with our Services, we may collect:
				</Paragraph>
				<Bullets>
					<li>
						<Strong>Account information:</Strong>
						<ul className="list-disc pl-6 mt-2 space-y-1">
							<li>Name</li>
							<li>Email address</li>
							<li>Phone number</li>
							<li>Password (stored securely in encrypted form)</li>
							<li>
								Professional role or account type (such as contractor,
								consultant, developer, architect, engineer, or supplier)
							</li>
						</ul>
					</li>
					<li>
						<Strong>Organisation information:</Strong>
						<ul className="list-disc pl-6 mt-2 space-y-1">
							<li>Company name</li>
							<li>Workspace or organisation membership</li>
							<li>Your role and permissions within a workspace</li>
						</ul>
					</li>
					<li>
						<Strong>Project information</Strong> — information created or uploaded
						while using Bankolé, including projects, tasks, subtasks, milestones,
						meetings, budgets, cost information, documents, RFIs, site diaries,
						project communications, reports, and activity history.
					</li>
					<li>
						<Strong>Site media</Strong> — photos, videos, documents, and other
						files uploaded or captured using your device.
					</li>
					<li>
						<Strong>Communications</Strong> — the content of team chat messages,
						comments, notifications, support conversations, feedback, contact
						form submissions, and demo requests.
					</li>
					<li>
						<Strong>Payment information</Strong> — if you purchase a subscription,
						payment details are processed by our third-party payment providers.
						We do not store your complete payment card information.
					</li>
				</Bullets>

				<SubHeading>1.2 Information collected automatically</SubHeading>
				<Paragraph>
					When you use our website or Services, we may automatically collect:
				</Paragraph>
				<Bullets>
					<li>IP address</li>
					<li>Browser type</li>
					<li>Device type</li>
					<li>Operating system</li>
					<li>App version</li>
					<li>Pages visited</li>
					<li>Features used</li>
					<li>Date and time of access</li>
					<li>Referring website</li>
					<li>Crash reports</li>
					<li>Diagnostic logs</li>
					<li>Network information</li>
				</Bullets>
				<Paragraph>
					If enabled, we may also collect notification delivery and read status.
				</Paragraph>

				<SubHeading>1.3 Location information</SubHeading>
				<Paragraph>
					If you grant permission through the mobile application, we may collect
					your device location to tag site photos, record project activity
					locations, and improve project documentation. You may disable location
					access at any time through your device settings.
				</Paragraph>

				<SubHeading>1.4 Cookies and similar technologies</SubHeading>
				<Paragraph>
					Our website uses cookies and similar technologies to keep you signed
					in, remember preferences, improve website performance, understand how
					visitors use our website, measure marketing effectiveness, and enhance
					security. You may disable cookies through your browser settings,
					although some features of the Services may not function properly.
				</Paragraph>

				<SubHeading>
					1.5 Biometric authentication (processed on your device only)
				</SubHeading>
				<Paragraph>
					If you enable Face ID, Touch ID, or fingerprint login within the mobile
					application, authentication is performed entirely by your device's
					operating system.{" "}
					<Strong>
						Your biometric information never leaves your device, is never
						transmitted to Bankolé, and is never stored on our servers.
					</Strong>{" "}
					We only store a local setting indicating whether biometric login has
					been enabled.
				</Paragraph>

				<SubHeading>1.6 Secure credential storage</SubHeading>
				<Paragraph>
					Authentication tokens and related credentials are stored using secure
					operating system storage mechanisms, including the iOS Keychain and
					Android Keystore.
				</Paragraph>

				<SectionHeading>2. How we use your information</SectionHeading>
				<Paragraph>We use your information to:</Paragraph>
				<Bullets>
					<li>create and manage your account;</li>
					<li>provide and operate the Services;</li>
					<li>authenticate users;</li>
					<li>manage organisations and workspaces;</li>
					<li>facilitate collaboration between project teams;</li>
					<li>manage construction projects and documentation;</li>
					<li>provide AI-powered features you choose to use (see Section 4);</li>
					<li>respond to enquiries and customer support requests;</li>
					<li>send important service notifications;</li>
					<li>improve our products and Services;</li>
					<li>analyse website usage;</li>
					<li>detect fraud and maintain security; and</li>
					<li>comply with legal obligations.</li>
				</Bullets>
				<Paragraph>
					We do <Strong>not</Strong> sell your personal information. We do{" "}
					<Strong>not</Strong> use your information for third-party behavioural
					advertising.
				</Paragraph>

				<SectionHeading>
					3. Legal bases for processing (EEA and UK users)
				</SectionHeading>
				<Paragraph>
					Where applicable, we process personal data based on:
				</Paragraph>
				<Bullets>
					<li>performance of a contract;</li>
					<li>legitimate business interests;</li>
					<li>your consent; and</li>
					<li>compliance with legal obligations.</li>
				</Bullets>

				<SectionHeading>4. AI-assisted features</SectionHeading>
				<Paragraph>
					Bankolé offers optional AI-powered features that assist with project
					management and productivity. When you use these features, we may
					process the prompts you submit and relevant project information
					required to generate responses. AI processing is performed solely to
					provide the requested functionality. Please avoid submitting highly
					sensitive information unless necessary for your intended use.
				</Paragraph>

				<SectionHeading>5. Marketing communications</SectionHeading>
				<Paragraph>
					If you subscribe to newsletters, request a demo, download resources, or
					otherwise opt in, we may send you product updates, educational content,
					newsletters, event invitations, and promotional communications. You may
					unsubscribe at any time by using the unsubscribe link included in our
					emails or by contacting us.
				</Paragraph>

				<SectionHeading>6. How we share information</SectionHeading>
				<Paragraph>
					We only share information where necessary to provide our Services. This
					includes:
				</Paragraph>
				<Bullets>
					<li>
						<Strong>Within your workspace:</Strong> project information, messages,
						documents, and media may be visible to authorised members of your
						organisation according to workspace permissions.
					</li>
					<li>
						<Strong>Service providers:</Strong> we work with trusted third-party
						providers that help us operate the Services, including providers for
						cloud hosting, authentication, analytics, customer support,
						communications, payment processing, and AI services. These providers
						process information only on our behalf and under contractual
						obligations.
					</li>
					<li>
						<Strong>Legal requirements:</Strong> we may disclose information where
						required to comply with law, respond to lawful requests, protect
						users, investigate fraud, enforce our agreements, and protect our
						rights.
					</li>
					<li>
						<Strong>Business transfers:</Strong> if Bankole Technologies is
						involved in a merger, acquisition, financing, or sale of assets,
						personal information may be transferred as part of that transaction.
					</li>
				</Bullets>

				<SectionHeading>7. Data retention</SectionHeading>
				<Paragraph>
					We retain personal information only for as long as necessary to provide
					the Services, maintain your account, comply with legal obligations,
					resolve disputes, and enforce agreements. If you delete your account,
					we will delete or anonymise personal information where legally
					permitted.{" "}
					<Strong>
						Content shared within an organisation or workspace (including
						projects, messages, and documents) may continue to be retained by
						that organisation
					</Strong>{" "}
					after your personal account is deleted.
				</Paragraph>

				<SectionHeading>8. Your rights</SectionHeading>
				<Paragraph>
					Depending on your location, you may have the right to:
				</Paragraph>
				<Bullets>
					<li>access your personal information;</li>
					<li>correct inaccurate information;</li>
					<li>delete your information;</li>
					<li>export your data;</li>
					<li>restrict certain processing;</li>
					<li>object to certain processing;</li>
					<li>withdraw consent; and</li>
					<li>lodge a complaint with your local data protection authority.</li>
				</Bullets>
				<Paragraph>
					You may exercise many of these rights through your account settings or
					by contacting us. You will not be discriminated against for exercising
					your privacy rights.
				</Paragraph>

				<SectionHeading>9. Managing your account</SectionHeading>
				<Paragraph>
					You may update your profile information, manage permissions, revoke
					device permissions, change notification preferences, and permanently
					delete your account. Where available, account deletion can be completed
					directly from your account settings. Deletion is permanent and cannot
					be reversed.
				</Paragraph>

				<SectionHeading>10. Security</SectionHeading>
				<Paragraph>
					We use industry-standard technical and organisational safeguards to
					protect your information, including:
				</Paragraph>
				<Bullets>
					<li>HTTPS/TLS encryption;</li>
					<li>encrypted data storage;</li>
					<li>secure authentication;</li>
					<li>access controls;</li>
					<li>monitoring and logging; and</li>
					<li>hardware-backed credential storage on supported devices.</li>
				</Bullets>
				<Paragraph>
					Despite our efforts, no method of electronic storage or transmission
					can be guaranteed to be completely secure.
				</Paragraph>

				<SectionHeading>11. Children's privacy</SectionHeading>
				<Paragraph>
					Our Services are intended for business and professional users and are
					not directed toward children under the age of 13 (or the applicable
					minimum age in your jurisdiction). We do not knowingly collect personal
					information from children. If we become aware that such information has
					been collected, we will promptly delete it.
				</Paragraph>

				<SectionHeading>12. International data transfers</SectionHeading>
				<Paragraph>
					Your information may be transferred to and processed in countries
					outside your country of residence. Where required by applicable law, we
					implement appropriate safeguards to protect such transfers.
				</Paragraph>

				<SectionHeading>13. Third-party services</SectionHeading>
				<Paragraph>
					Our website and Services may contain links to third-party websites or
					integrate with third-party services. We are not responsible for the
					privacy practices of those third parties. We encourage you to review
					their privacy policies before providing personal information.
				</Paragraph>

				<SectionHeading>14. Changes to this Privacy Policy</SectionHeading>
				<Paragraph>
					We may update this Privacy Policy from time to time. When we do, we
					will update the "Last updated" date above and, where appropriate,
					notify users through the website, email, or within the Services.
				</Paragraph>

				<SectionHeading>15. Contact us</SectionHeading>
				<Paragraph>
					<Strong>Bankole Technologies</Strong>
				</Paragraph>
				<Bullets>
					<li>
						Website:{" "}
						<a
							href="https://www.usebankole.com"
							target="_blank"
							rel="noreferrer"
							className="text-bblue font-SatoshiMedium hover:underline"
						>
							https://www.usebankole.com
						</a>
					</li>
					<li>
						Email: <MailLink email="privacy@usebankole.com" />
					</li>
					<li>
						Support: <MailLink email="support@usebankole.com" />
					</li>
				</Bullets>
			</div>
		</section>
	);
};

export default PrivacyContent;
